import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, CalendarClock } from 'lucide-react';
import { RENEWAL_KIND_LABEL, describeDaysLeft, renewalUrgency } from '@bits/domain';
import type { RenewalSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatRenewalDate } from '@/components/renewals';

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const cardLink =
  'block rounded-[18px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-4 focus-visible:ring-offset-[hsl(var(--background))]';

// The card lifts toward the pointer rather than tinting, so the grid stays
// monochrome and the only colour left is the figure that needs attention.
const statCard =
  'h-full transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(16,24,40,0.05),0_16px_32px_-16px_rgba(16,24,40,0.18)] motion-reduce:transition-none motion-reduce:hover:translate-y-0';

const RENEWAL_WINDOW_DAYS = 60;

function DashboardPage(): JSX.Element {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    retry: false,
  });
  const queueQuery = useQuery({
    queryKey: ['queue-items'],
    queryFn: () => api.listQueueItems(),
    retry: false,
  });
  const isAdmin = (meQuery.data?.person.roles ?? []).includes('Admin');
  const renewalsQuery = useQuery({
    queryKey: ['renewals-due', RENEWAL_WINDOW_DAYS],
    queryFn: () => api.listDueRenewals(RENEWAL_WINDOW_DAYS),
    enabled: isAdmin,
    retry: false,
  });

  const firstName = (meQuery.data?.person.name ?? '').split(' ')[0] ?? '';
  const overdueCount = tasksQuery.data?.overdue.length ?? 0;
  const dueTodayCount = tasksQuery.data?.today.length ?? 0;
  const queueTotal = queueQuery.data?.total ?? 0;
  const dueRenewals = renewalsQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
          {firstName ? <>Welcome back, {firstName}.</> : <>Welcome.</>}
        </h1>
        <p className="mt-2 max-w-2xl text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
          Your operations, in one place.
        </p>
      </div>

      {isAdmin && dueRenewals.length > 0 ? <RenewalsBanner items={dueRenewals} /> : null}

      <div className="grid gap-5 md:grid-cols-3">
        <Link to="/my-tasks" className={cardLink}>
          <Card className={statCard}>
            <CardContent className="flex h-full flex-col p-7">
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                My tasks
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <span
                  className={
                    overdueCount > 0
                      ? 'font-display text-6xl font-semibold tabular-nums text-[hsl(var(--destructive))]'
                      : 'font-display text-6xl font-semibold tabular-nums'
                  }
                >
                  {overdueCount}
                </span>
                <span className="text-base text-[hsl(var(--muted-foreground))]">overdue</span>
              </div>
              <span className="mt-auto pt-4 text-sm text-[hsl(var(--muted-foreground))]">
                {dueTodayCount} due today
              </span>
            </CardContent>
          </Card>
        </Link>

        <Link to="/my-queue" className={cardLink}>
          <Card className={statCard}>
            <CardContent className="flex h-full flex-col p-7">
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                My queue
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-6xl font-semibold tabular-nums">
                  {queueTotal}
                </span>
                <span className="text-base text-[hsl(var(--muted-foreground))]">open</span>
              </div>
              <span className="mt-auto pt-4 text-sm text-[hsl(var(--muted-foreground))]">
                Approvals, mentions and reminders
              </span>
            </CardContent>
          </Card>
        </Link>

        <Link to="/sales/dashboard" className={cardLink}>
          <Card className={statCard}>
            <CardContent className="flex h-full flex-col p-7">
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                Sales pipeline
              </span>
              <span className="mt-6 font-display text-2xl font-semibold leading-snug">
                Prospects, conversion and stale items
              </span>
              <span className="mt-auto pt-4 text-sm font-medium text-[hsl(var(--brand))]">
                Open dashboard
              </span>
            </CardContent>
          </Card>
        </Link>
      </div>
    </AppShell>
  );
}

const MAX_BANNER_ROWS = 6;

function RenewalsBanner({ items }: { items: RenewalSummary[] }): JSX.Element {
  const overdue = items.filter((r) => (r.daysLeft ?? 0) < 0).length;
  const shown = items.slice(0, MAX_BANNER_ROWS);
  const hidden = items.length - shown.length;
  const headline =
    overdue > 0
      ? `${overdue} ${overdue === 1 ? 'renewal has' : 'renewals have'} expired`
      : `${items.length} ${items.length === 1 ? 'renewal is' : 'renewals are'} due within ${RENEWAL_WINDOW_DAYS} days`;

  return (
    <section
      role="alert"
      aria-label="Renewals needing attention"
      className="overflow-hidden rounded-[18px] border-2 border-[hsl(var(--destructive))]/50 bg-[hsl(var(--card))] shadow-[0_1px_2px_rgba(212,50,60,0.08),0_12px_32px_-12px_rgba(212,50,60,0.25)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[hsl(var(--destructive))]/20 bg-[hsl(var(--destructive))]/8 px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]">
            <AlertTriangle className="size-5" aria-hidden />
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--destructive))]">
              Important
            </span>
            <span className="font-display text-lg font-semibold">{headline}</span>
          </div>
        </div>
        <Link
          to="/renewals"
          className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--brand))] hover:underline"
        >
          Open Renewals
          <ArrowRight className="size-4" aria-hidden />
        </Link>
      </div>
      <ul className="divide-y divide-[hsl(var(--border))]">
        {shown.map((renewal) => {
          const daysLeft = renewal.daysLeft ?? 0;
          const urgency = renewalUrgency(daysLeft);
          const hot = urgency === 'expired' || urgency === 'today' || urgency === 'critical';
          return (
            <li key={renewal.id}>
              <Link
                to={`/renewals#${renewal.id}` as never}
                className="group flex items-center gap-4 px-6 py-3 transition-colors hover:bg-[hsl(var(--accent))]/40 focus-visible:bg-[hsl(var(--accent))]/40 focus-visible:outline-none"
              >
                <span
                  aria-hidden
                  className={cn(
                    'size-2.5 shrink-0 rounded-full',
                    hot ? 'bg-[hsl(var(--destructive))]' : 'bg-amber-500',
                  )}
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">
                    {renewal.label}
                    <span className="font-normal text-[hsl(var(--muted-foreground))]">
                      {' '}
                      · {RENEWAL_KIND_LABEL[renewal.kind]}
                      {renewal.provider ? ` · ${renewal.provider}` : ''}
                    </span>
                  </span>
                  <span className="truncate text-xs text-[hsl(var(--muted-foreground))]">
                    {renewal.project
                      ? `${renewal.project.code} · ${renewal.project.name}${renewal.client ? ` · ${renewal.client.displayName}` : ''}`
                      : renewal.client
                        ? `For ${renewal.client.displayName}`
                        : 'Company-level, not linked to a project'}
                  </span>
                </span>
                <span className="hidden shrink-0 items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] sm:inline-flex">
                  <CalendarClock className="size-3.5" aria-hidden />
                  {renewal.expiresOn ? formatRenewalDate(renewal.expiresOn) : '—'}
                </span>
                <span
                  className={cn(
                    'shrink-0 text-sm font-semibold tabular-nums',
                    hot ? 'text-[hsl(var(--destructive))]' : 'text-amber-700',
                  )}
                >
                  {describeDaysLeft(daysLeft)}
                </span>
                <ArrowRight
                  className="size-4 shrink-0 text-[hsl(var(--muted-foreground))] transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            </li>
          );
        })}
      </ul>
      {hidden > 0 ? (
        <div className="border-t border-[hsl(var(--border))] px-6 py-3 text-sm text-[hsl(var(--muted-foreground))]">
          {hidden} more due within {RENEWAL_WINDOW_DAYS} days on the{' '}
          <Link to="/renewals" className="font-medium text-[hsl(var(--brand))] hover:underline">
            Renewals page
          </Link>
          .
        </div>
      ) : null}
    </section>
  );
}
